import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Button,
  Alert,
  Skeleton,
  Chip
} from '@mui/material';
import {
  ArrowBack,
  Email,
  Assignment,
  Update,
  GroupAdd,
  Event,
  Support,
  NotificationsOff,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/api';

const PREF_CONFIG = [
  {
    key: 'task_assigned',
    icon: <Assignment color="primary" />,
    label: 'Tâches assignées',
    description: 'Recevoir un email quand une tâche vous est assignée'
  },
  {
    key: 'task_status_update',
    icon: <Update color="info" />,
    label: 'Mises à jour de tâches',
    description: 'Recevoir un email quand le statut d\'une de vos tâches change'
  },
  {
    key: 'project_invitation',
    icon: <GroupAdd color="success" />,
    label: 'Invitations & demandes de projet',
    description: 'Recevoir un email lors d\'une invitation à rejoindre un projet ou d\'une demande d\'accès'
  },
  {
    key: 'meeting_invitation',
    icon: <Event sx={{ color: '#9333ea' }} />,
    label: 'Invitations aux réunions',
    description: 'Recevoir un email quand vous êtes invité à une réunion'
  },
  {
    key: 'ticket_updates',
    icon: <Support color="warning" />,
    label: 'Tickets de support',
    description: 'Recevoir des emails concernant vos tickets de support'
  }
];

function NotificationPreferencesPage() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    notificationService.getEmailPreferences()
      .then(res => setPrefs(res.data.data))
      .catch(() => setError('Impossible de charger les préférences.'))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (key) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSaving(true);
    setSaveSuccess(false);
    try {
      const res = await notificationService.updateEmailPreferences({ [key]: !prefs[key] });
      setPrefs(res.data.data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch {
      setPrefs(prefs); // rollback
      setError('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  const handleMasterToggle = async () => {
    const updated = { ...prefs, email_enabled: !prefs.email_enabled };
    setPrefs(updated);
    setSaving(true);
    setSaveSuccess(false);
    try {
      const res = await notificationService.updateEmailPreferences({ email_enabled: !prefs.email_enabled });
      setPrefs(res.data.data);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch {
      setPrefs(prefs);
      setError('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/settings')}
        sx={{ mb: 2 }}
      >
        Retour aux paramètres
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Email sx={{ fontSize: 32, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" fontWeight="bold">Préférences de notifications email</Typography>
          <Typography variant="body2" color="text.secondary">
            Choisissez les emails que vous souhaitez recevoir d'ERA Project
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {saveSuccess && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
          Préférences sauvegardées automatiquement
        </Alert>
      )}

      {/* Master switch */}
      <Paper elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <ListItem sx={{ py: 2, px: 3 }}>
          <ListItemIcon>
            {prefs && !prefs.email_enabled
              ? <NotificationsOff color="error" />
              : <Email color="primary" />}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Notifications email
                </Typography>
                {prefs && !prefs.email_enabled && (
                  <Chip label="Désactivées" color="error" size="small" />
                )}
              </Box>
            }
            secondary="Interrupteur principal — désactiver pour ne plus recevoir aucun email d'ERA Project"
          />
          <ListItemSecondaryAction>
            {loading
              ? <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 12 }} />
              : <Switch
                  edge="end"
                  checked={!!prefs?.email_enabled}
                  onChange={handleMasterToggle}
                  disabled={saving}
                />
            }
          </ListItemSecondaryAction>
        </ListItem>
      </Paper>

      {/* Granular switches */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6">Types de notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            Ces préférences s'appliquent uniquement si les notifications email sont activées
          </Typography>
        </Box>
        <List disablePadding>
          {PREF_CONFIG.map((item, index) => (
            <React.Fragment key={item.key}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                sx={{
                  py: 2,
                  px: 3,
                  opacity: prefs && !prefs.email_enabled ? 0.5 : 1,
                  transition: 'opacity 0.2s'
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={item.description}
                />
                <ListItemSecondaryAction>
                  {loading
                    ? <Skeleton variant="rectangular" width={50} height={24} sx={{ borderRadius: 12 }} />
                    : <Switch
                        edge="end"
                        checked={!!prefs?.[item.key]}
                        onChange={() => handleToggle(item.key)}
                        disabled={saving || (prefs && !prefs.email_enabled)}
                      />
                  }
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Les modifications sont sauvegardées automatiquement. Les emails transactionnels (vérification de compte, réinitialisation de mot de passe) sont toujours envoyés indépendamment de ces préférences.
        </Typography>
      </Box>
    </Container>
  );
}

export default NotificationPreferencesPage;
